import { FC, useEffect, ReactNode } from 'react';
import { useRefetchableFragment, graphql } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { SingleSelect, SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';

import { CategoryL2Select_viewer$key } from './__generated__/CategoryL2Select_viewer.graphql';
import { CategoryL2SelectRefetchQuery } from './__generated__/CategoryL2SelectRefetchQuery.graphql';

import { VerticalType } from '../SellerApplicationFormReducer';

const messages = defineMessages({
    subCategory: {
        id: 'SellerApplicationForm.categoryL2Select.subCategory',
        defaultMessage: 'Item Sub Category',
    },
});

type Props = {
    viewer: CategoryL2Select_viewer$key;
    vertical: VerticalType | null;
    onChange: (option: SingleOnChangeArgs) => void;
    value: string;
    disabled: boolean;
    errorMessage: ReactNode;
};

const verticalsMap: { [key: string]: string } = {
    Furniture: 'F',
    Fashion: 'V',
    Art: 'A',
    Jewelry: 'J',
};

const CategoryL2Select: FC<Props> = ({
    viewer: viewerRef,
    vertical,
    onChange,
    value,
    disabled,
    errorMessage,
}) => {
    const intl = useIntl();
    const [data, refetch] = useRefetchableFragment<
        CategoryL2SelectRefetchQuery,
        CategoryL2Select_viewer$key
    >(
        graphql`
            fragment CategoryL2Select_viewer on Viewer
            @argumentDefinitions(vertical: { type: "[String]", defaultValue: [] })
            @refetchable(queryName: "CategoryL2SelectRefetchQuery") {
                categories(categoryKeys: $vertical) {
                    key
                    children {
                        key
                        name
                        children {
                            key
                            name
                        }
                    }
                }
            }
        `,
        viewerRef
    );

    useEffect(() => {
        if (vertical) {
            refetch({ vertical: [verticalsMap[vertical]] });
        }
    }, [vertical, refetch]);

    const categories = data?.categories?.[0]?.children || [];

    const l2Options = categories.map(l2Obj => ({
        label: l2Obj?.name || '',
        value: l2Obj?.key || '',
    }));

    return (
        <SingleSelect
            dataTn="item-sub-category-select"
            name="item-sub-category-select"
            onChange={onChange}
            placeholder={intl.formatMessage(messages.subCategory)}
            options={l2Options}
            value={value}
            clearable={false}
            disabled={disabled}
            errorMessage={errorMessage}
            hasError={!!errorMessage}
        />
    );
};

export default CategoryL2Select;
